/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { oktaServerSuggestionsRestore } from "../funcs/oktaServerSuggestionsRestore.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OktaServerSuggestion } from "../models/components/oktaserversuggestion.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  RestoreOktaServerSuggestionRequest,
  RestoreOktaServerSuggestionSecurity,
} from "../models/operations/restoreoktaserversuggestion.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type RestoreOktaServerSuggestionMutationVariables = {
  request: RestoreOktaServerSuggestionRequest;
  security?: RestoreOktaServerSuggestionSecurity | undefined;
  options?: RequestOptions;
};

export type RestoreOktaServerSuggestionMutationData = OktaServerSuggestion;

export type RestoreOktaServerSuggestionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * restore oktaServerSuggestions
 *
 * @remarks
 * Restore a dismissed suggestion currently made to the organization. Requires org:admin and the okta-connections rollout.
 */
export function useRestoreOktaServerSuggestionMutation(
  options?: MutationHookOptions<
    RestoreOktaServerSuggestionMutationData,
    RestoreOktaServerSuggestionMutationError,
    RestoreOktaServerSuggestionMutationVariables
  >,
): UseMutationResult<
  RestoreOktaServerSuggestionMutationData,
  RestoreOktaServerSuggestionMutationError,
  RestoreOktaServerSuggestionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildRestoreOktaServerSuggestionMutation(client, options),
    ...options,
  });
}

export function mutationKeyRestoreOktaServerSuggestion(): MutationKey {
  return ["@gram/client", "oktaServerSuggestions", "restore"];
}

export function buildRestoreOktaServerSuggestionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: RestoreOktaServerSuggestionMutationVariables,
  ) => Promise<RestoreOktaServerSuggestionMutationData>;
} {
  return {
    mutationKey: mutationKeyRestoreOktaServerSuggestion(),
    mutationFn: function restoreOktaServerSuggestionMutationFn({
      request,
      security,
      options,
    }): Promise<RestoreOktaServerSuggestionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(oktaServerSuggestionsRestore(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
